import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { openModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { expandSection } from 'features/shared/collapseStorage';

import { getSkuCollapseKey, STATUS_TO_BOOLEANS } from './SkuItems.constants';
import { getItemCategoryIds, getItemId, getItemName } from './SkuMenuData.normalizers';
import { SKU_ADD_ITEM_FLYOUT_ID } from './components/SkuAddItemForm';
import { SKU_EDIT_ITEM_FLYOUT_ID } from './components/SkuEditItemForm';
import { SKU_REMOVE_ITEM_FROM_CATEGORY_MODAL_NAME } from './components/SkuRemoveItemFromCategoryModal';
import useSkuMutations from './useSkuMutations';
import messages from './SkuMenuPage.messages';

const useSkuItemActions = ({ venueId, refetch, openFlyout }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { statusChange, removeFromCategory, removeFromAllCategories } = useSkuMutations({ venueId });

  const [preselectedCategoryIds, setPreselectedCategoryIds] = useState([]);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [preselectedEditCategoryIds, setPreselectedEditCategoryIds] = useState([]);

  const handleAddItemClick = category => {
    setPreselectedCategoryIds(category?.id ? [category.id] : []);
    openFlyout?.(SKU_ADD_ITEM_FLYOUT_ID);
  };

  const handleAddItemSuccess = () => {
    const storageKey = getSkuCollapseKey(venueId);
    preselectedCategoryIds.forEach(catId => expandSection(storageKey, `cat-${catId}`));
    refetch();
  };

  const handleEditItemClick = (item, category) => {
    const selectedItem = item?.raw || item || null;
    setSelectedItemForEdit(selectedItem);
    const itemCategoryIds = getItemCategoryIds(item?.raw || item);
    const fallbackCategoryIds = category?.id ? [category.id] : [];
    setPreselectedEditCategoryIds(itemCategoryIds.length ? itemCategoryIds : fallbackCategoryIds);
    openFlyout?.(SKU_EDIT_ITEM_FLYOUT_ID);
  };

  const handleEditItemSuccess = () => {
    // Refetch needed: edit form syncs category relationships server-side, not captured by optimistic update
    refetch();
  };

  const handleRemoveFromSingleCategory = useCallback(
    async (normalizedItem, categoryId, itemName) => {
      const itemId = getItemId(normalizedItem);
      await removeFromCategory.mutateAsync(
        { itemId, categoryId },
        {
          onSuccess: () => {
            dispatch(
              createSuccessSnackbar(
                messages.removeItemSuccess,
                { itemName, strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.removeItemError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, removeFromCategory]
  );

  const handleRemoveFromAllCategories = useCallback(
    async (normalizedItem, itemName) => {
      const itemId = getItemId(normalizedItem);
      const categoryIds = getItemCategoryIds(normalizedItem);
      await removeFromAllCategories.mutateAsync(
        { itemId, categoryIds },
        {
          onSuccess: () => {
            dispatch(
              createSuccessSnackbar(
                messages.removeFromAllMenusSuccess,
                { itemName, strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.removeItemError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, removeFromAllCategories]
  );

  const handleStatusChange = useCallback(
    (item, nextStatus) => {
      const normalizedItem = item?.raw || item || null;
      const itemId = getItemId(normalizedItem);
      if (!itemId) return;

      const statusBooleans = STATUS_TO_BOOLEANS[nextStatus];
      if (!statusBooleans) return;
      const { enabled, soldOut, partnerState } = statusBooleans;

      statusChange.mutate(
        {
          itemId,
          optimistic: { enabled, soldOut, partnerState },
          payload: {
            name: normalizedItem?.name || '',
            description: normalizedItem?.description || '',
            enabled,
            soldOut,
            partnerState,
          },
        },
        {
          onSuccess: () => {
            dispatch(
              createSuccessSnackbar(
                messages.itemStatusChangeSuccess,
                {
                  itemName: getItemName(normalizedItem),
                  strong: chunks => <strong>{chunks}</strong>,
                },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.itemStatusChangeError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, statusChange]
  );

  const handleDeleteItem = useCallback(
    (item, category) => {
      const normalizedItem = item?.raw || item || null;
      const itemId = getItemId(normalizedItem);
      const itemName = getItemName(normalizedItem) || intl.formatMessage(messages.itemLabel);
      const categoryId = category?.id;
      if (!itemId || !categoryId) {
        return;
      }

      const categoryCount = getItemCategoryIds(normalizedItem).length;

      dispatch(
        openModal(SKU_REMOVE_ITEM_FROM_CATEGORY_MODAL_NAME, {
          itemName,
          categoryName: category.name,
          categoryCount,
          onRemoveFromCategory: () => handleRemoveFromSingleCategory(normalizedItem, categoryId, itemName),
          onRemoveFromAllMenus: () => handleRemoveFromAllCategories(normalizedItem, itemName),
        })
      );
    },
    [dispatch, handleRemoveFromSingleCategory, handleRemoveFromAllCategories, intl]
  );

  return {
    preselectedCategoryIds,
    selectedItemForEdit,
    preselectedEditCategoryIds,
    handleAddItemClick,
    handleAddItemSuccess,
    handleEditItemClick,
    handleEditItemSuccess,
    handleStatusChange,
    handleDeleteItem,
  };
};

export default useSkuItemActions;
